import React, { useCallback, useState } from 'react';
import { Modal } from 'antd';
import { TagManager } from 'ontraccr-common';
import PropTypes from 'prop-types';

import { TagView } from '../dashboard/LiveFeed/LiveFeedTaggedText';
import LiveFeedFileAddButton from '../dashboard/LiveFeed/LiveFeedFileAddButton';
import LiveFeedFileUpload from '../dashboard/LiveFeed/LiveFeedFileUpload';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import ProfileAvatar from '../common/ProfileAvatar';
import PhotoReel from '../common/photos/PhotoReel';

const inputMetrics = {
  padding: '4px 11px',
  lineHeight: '22px',
  fontSize: 14,
  textAlign: 'left',
};

export default function NoteEditModal({
  note,
  onClose,
  onSave,
  users = [],
  userTrie = {},
  userMap = {},
  allowTags = false,
  allowUpload = false,
  fileMap = {},
}) {
  const [text, setText] = useState('');
  const [tagManager, setTagManager] = useState();
  const [parsedText, setParsedText] = useState();
  const [searchResults, setSearchResults] = useState();
  const [saving, setSaving] = useState(false);
  const [keptFiles, setKeptFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [prevNoteId, setPrevNoteId] = useState();

  const parseTags = useCallback((nextText, manager) => {
    if (!manager || !allowTags) {
      setParsedText(nextText);
      setSearchResults();
      return;
    }
    const {
      text: nextParsed,
      searchResults: nextSearch,
    } = manager.parseText(nextText);
    setParsedText(nextParsed);
    setSearchResults(nextSearch);
  }, [allowTags]);

  const resetEditor = () => {
    setText('');
    setTagManager();
    setParsedText();
    setSearchResults();
    setKeptFiles([]);
    setNewFiles([]);
    setRemovedFileIds([]);
    setUploadOpen(false);
  };

  const initializeFromNote = (nextNote) => {
    const manager = new TagManager({
      trie: userTrie,
      data: users.filter((user) => user.active),
      basicView: ({ children }) => children,
      tagView: TagView,
    });
    if (allowTags) {
      (nextNote.tags || []).forEach(({ startIndex, userId }) => {
        const user = userMap[userId];
        if (user) manager.addTag(startIndex, user);
      });
    }
    const nextText = nextNote.note || '';
    setTagManager(manager);
    setText(nextText);
    if (nextText) {
      parseTags(nextText, manager);
    } else {
      setParsedText(nextText);
      setSearchResults();
    }
    setKeptFiles(nextNote.files || []);
    setNewFiles([]);
    setRemovedFileIds([]);
    setUploadOpen(false);
  };

  if (note?.id !== prevNoteId) {
    setPrevNoteId(note?.id);
    if (note) {
      initializeFromNote(note);
    } else {
      resetEditor();
    }
  }

  const onTextChange = (e) => {
    const {
      target: {
        value: nextText,
      } = {},
    } = e;
    setText(nextText);
    parseTags(nextText, tagManager);
  };

  const selectTag = (user) => () => {
    if (!text || !tagManager) return;
    const index = text.lastIndexOf('@');
    if (index < 0) return;
    const nextText = `${text.slice(0, index)}@${user.name}`;
    tagManager.addTag(index, user);
    const { text: nextParsed } = tagManager.parseText(nextText);
    setText(nextText);
    setParsedText(nextParsed);
    setSearchResults();
  };

  const hasFiles = keptFiles.length > 0 || newFiles.length > 0;
  const canSave = Boolean(text?.trim()) || hasFiles;
  const keptPreviews = keptFiles.map((file) => ({
    ...file,
    ...fileMap[file.id],
  }));

  const removeKeptFile = (index) => {
    const removed = keptFiles[index];
    if (removed?.id) {
      setRemovedFileIds((ids) => (ids.includes(removed.id) ? ids : [...ids, removed.id]));
    }
    setKeptFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  };

  const removeNewFile = (index) => {
    setNewFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  };

  const addNewFile = (file) => {
    setNewFiles((current) => [...current, file]);
  };

  const onOk = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    const tags = allowTags && tagManager ? tagManager.getTaskMapPayload() : (note?.tags || []);
    const saved = await onSave({
      text,
      tags,
      files: newFiles,
      removedFileIds,
    });
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Modal
      title="Edit Note"
      visible={!!note}
      onCancel={saving ? undefined : onClose}
      onOk={onOk}
      okText="Save"
      confirmLoading={saving}
      okButtonProps={{ disabled: !canSave }}
      destroyOnClose
      zIndex={1100}
      maskClosable={!saving}
    >
      <div style={{ position: 'relative' }}>
        {allowTags && (
          <div
            style={{
              position: 'absolute',
              top: 1,
              right: 1,
              bottom: 1,
              left: 1,
              ...inputMetrics,
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              zIndex: 1,
            }}
          >
            {parsedText}
          </div>
        )}
        <OnTraccrTextInput
          textarea
          autoSize={{ minRows: 4 }}
          value={text}
          onChange={onTextChange}
          placeholder="Edit note"
          style={{
            ...inputMetrics,
            background: 'transparent',
            color: allowTags && text ? 'transparent' : undefined,
            caretColor: 'black',
            position: 'relative',
            zIndex: 2,
          }}
          onBlur={() => {
            setTimeout(() => setSearchResults(), 150);
          }}
        />
        {allowTags && searchResults && searchResults.length > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              zIndex: 5,
              background: 'white',
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {searchResults.map((user) => (
              <button
                type="button"
                key={user.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={selectTag(user)}
                style={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  width: '100%',
                  textAlign: 'left',
                  background: 'white',
                  border: 'none',
                }}
              >
                <ProfileAvatar
                  name={user.name}
                  role={user.position}
                  avatarRadius={12}
                />
              </button>
            ))}
          </div>
        )}
      </div>
      {hasFiles && (
        <div style={{ marginTop: 16 }}>
          {keptPreviews.length > 0 && (
            <PhotoReel
              photos={keptPreviews}
              small
              shadow
              onDelete={saving ? undefined : removeKeptFile}
            />
          )}
          {newFiles.length > 0 && (
            <PhotoReel
              files={newFiles}
              small
              shadow
              onDelete={saving ? undefined : removeNewFile}
            />
          )}
        </div>
      )}
      {allowUpload && (
        <div style={{ marginTop: 16 }}>
          <LiveFeedFileAddButton
            open
            useIcon={false}
            onClick={() => {
              if (!saving) setUploadOpen(true);
            }}
          />
          <LiveFeedFileUpload
            shouldDownload
            visible={uploadOpen}
            onUploadEnd={() => setUploadOpen(false)}
            addFile={addNewFile}
            placement="bottom"
            height={360}
            style={{ position: 'fixed', zIndex: 1300 }}
            customProps={{ zIndex: 1300 }}
          />
        </div>
      )}
    </Modal>
  );
}

NoteEditModal.propTypes = {
  note: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  users: PropTypes.array,
  userTrie: PropTypes.object,
  userMap: PropTypes.object,
  allowTags: PropTypes.bool,
  allowUpload: PropTypes.bool,
  fileMap: PropTypes.object,
};
